import { STORAGE_KEYS } from "@/lib/storage-keys";
import { markPageUnloading } from "@/services/api";

export const APP_BUILD_ID = __APP_BUILD_ID__;

const VERSION_URL = "/version.json";
const MIN_INTERVAL_MS = 60_000;

let staleBuildId: string | null = null;
let lastCheckAt = 0;
const listeners = new Set<() => void>();

export function subscribeToStaleBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isBuildStale(): boolean {
  return staleBuildId !== null;
}

/**
 * Une SPA ne recharge jamais `index.html` d'elle-même : sans cette vérification réseau, un
 * onglet resté ouvert exécute indéfiniment le build avec lequel il a démarré.
 */
export async function checkForNewBuild(): Promise<void> {
  if (staleBuildId || import.meta.env.DEV) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  const now = Date.now();
  if (now - lastCheckAt < MIN_INTERVAL_MS) return;
  lastCheckAt = now;

  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return;
    const { buildId } = (await res.json()) as { buildId?: string };
    if (!buildId || buildId === APP_BUILD_ID) return;

    staleBuildId = buildId;
    listeners.forEach((listener) => listener());
  } catch {
    // Hors ligne ou réponse illisible : sans effet, la prochaine vérification retentera.
  }
}

/**
 * Recharge seulement si l'onglet ne l'a pas déjà fait pour ce build : un `version.json` qui
 * annoncerait une version que le rechargement ne délivre pas boucherait sinon à l'infini.
 * Stockage inaccessible → pas de garde-fou possible, donc pas de rechargement automatique.
 */
export function autoReloadForNewBuild(): void {
  if (!staleBuildId) return;
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.BUILD_RELOAD_TARGET) === staleBuildId) return;
    sessionStorage.setItem(STORAGE_KEYS.BUILD_RELOAD_TARGET, staleBuildId);
  } catch {
    return;
  }
  reloadNow();
}

/**
 * Recharge en signalant d'abord la sortie de page.
 *
 * Un rechargement annule les requêtes en vol, et une réponse dont le corps était en cours de lecture
 * échoue alors en `TypeError` — que la couche API enveloppe légitimement en coupure réseau. Sans ce
 * marquage, chaque requête interrompue par notre propre rechargement remontait comme un incident.
 *
 * Constaté le 20/08 : un `GET /v1/conversations` **arrivé en 200** signalé en erreur réseau, à la
 * seconde où la sonde de version venait de détecter un nouveau build. Le rechargement d'un
 * déploiement produisait donc une rafale de fausses alertes, précisément au moment où l'on regarde
 * si le déploiement s'est bien passé.
 */
export function reloadNow(): void {
  markPageUnloading();
  window.location.reload();
}
